import type {
  Order,
  OrderDestination,
  VehicleTypes,
} from '@hero/schema';
import type {
  PublishOrderCustomerDraft,
  PublishOrderLoadDraft,
  PublishOrderLocationDraft,
  PublishOrderPickupRange,
  PublishOrderPriceOverride,
} from '../../usePublishOrderStore';

/**
 * Subset of `OrderDraftState` populated from a persisted `Order`.
 * Edit / reorder surfaces hydrate the draft store with this and
 * keep the remainder at store defaults.
 */
export interface OrderDraftHydration {
  customer: PublishOrderCustomerDraft | null;
  isInternalShipment: boolean;
  origin: PublishOrderLocationDraft | null;
  destinations: PublishOrderLocationDraft[];
  destinationVehicleQuantities: number[];
  pickupDate: PublishOrderPickupRange | null;
  load: PublishOrderLoadDraft;
  instructions?: string;
  priceOverrides: Array<PublishOrderPriceOverride | undefined>;
}

const stopToLocation = (
  stop: OrderDestination | undefined,
): PublishOrderLocationDraft | null => {
  if (!stop) return null;
  const coords = stop.address?.location?.coordinates;
  if (!coords || coords.length < 2) return null;
  return {
    lat: coords[1],
    lng: coords[0],
    name: stop.savedPlace?.name ?? stop.address?.fullAddress ?? '',
    city: stop.address?.city?.name,
    fullAddress: stop.address?.fullAddress,
    savedLocationId: stop.savedPlace?._id ? String(stop.savedPlace._id) : undefined,
    isAccurate: stop.isAccurateLocation ?? Boolean(stop.savedPlace?._id),
    locationUrl: stop.locationUrl,
  };
};

/**
 * Pull a `Partial<OrderDraftState>` out of a persisted Order for
 * edit / reorder flows. The shared draft layer is the single source
 * of truth — the edit drawer, reorder modal, and publish page all
 * consume the same shape, only the commit action differs.
 *
 * Note: this is a single-order hydration. A reorder flow that wants
 * to keep one truck (publish default) overrides
 * `destinationVehicleQuantities[0] = 1` after hydration; the source
 * order's vehicle count is intentionally not propagated.
 */
export const fromOrder = (order: Order): OrderDraftHydration => {
  const pickupStop = order.destinations?.find(
    (d: OrderDestination) => d.type === 'pickup',
  );
  const dropoffStop = order.destinations?.find(
    (d: OrderDestination) => d.type === 'dropoff',
  );

  const origin = stopToLocation(pickupStop);
  const dropoff = stopToLocation(dropoffStop);
  const destinations: PublishOrderLocationDraft[] = dropoff
    ? [dropoff]
    : [{ name: '', lat: 0, lng: 0 }];

  const pickupDate: PublishOrderPickupRange | null = order.dates?.pickup?.from
    ? {
        from: new Date(order.dates.pickup.from).toISOString(),
        to: new Date(order.dates.pickup.to ?? order.dates.pickup.from).toISOString(),
      }
    : null;

  const vehicles = Array.isArray(order.load?.vehicles)
    ? (order.load!.vehicles as VehicleTypes[])
    : undefined;

  const firstItem = order.load?.items?.[0];

  const load: PublishOrderLoadDraft = {
    vehicleType: vehicles?.[0],
    vehicleTypes: vehicles,
    temperatureType: order.load?.temperature?.types?.[0],
    temperatureDegree: order.load?.temperature?.degree
      ? {
          min: order.load.temperature.degree.min,
          max: order.load.temperature.degree.max,
        }
      : undefined,
    itemType: firstItem?.type,
    itemDescription: firstItem?.name,
    quantity: firstItem?.quantity,
    packing: firstItem?.packing,
    weightTon: firstItem?.weights?.value,
    itemCost: firstItem?.cost,
  };

  const isInternal = Boolean(
    order.client?._id &&
      order.shipper?._id &&
      String(order.client._id) === String(order.shipper._id),
  );

  const customer: PublishOrderCustomerDraft | null =
    !isInternal && order.client
      ? {
          customerId: order.client._id ? String(order.client._id) : undefined,
          name: order.client.name ?? '',
        }
      : null;

  return {
    customer,
    isInternalShipment: isInternal,
    origin,
    destinations,
    destinationVehicleQuantities: [1],
    pickupDate,
    load,
    instructions: order.instructions,
    priceOverrides: [undefined],
  };
};

/**
 * Reorder hydration — same as `fromOrder` but always starts at 1
 * truck (the publish UX default for reorder), even if the source
 * order had a multi-truck lane.
 */
export const fromOrderAgainSource = (order: Order): OrderDraftHydration => {
  const hydration = fromOrder(order);
  return {
    ...hydration,
    destinationVehicleQuantities: [1],
  };
};
